import { routeLink, extractLink, awaitMeetingPlatforms, supportedPlatformsText } from '../routeLink';
import { fileSource, speakerParams } from './file';
import { SUPPORTED_VIDEO_PLATFORMS } from '../../videoPlatforms';
import { logUiEvent as defaultLog } from '../../../services/uiLogger';

/**
 * Источник «ссылка»: запись с видеохостинга скачивает bit-fetch и передаёт
 * дальше по конвейеру сам: русскую речь — в bit-transcribe-v3, другие языки —
 * в parakeet, а «по участникам» — в bit-diarize-v3.
 *
 * 🔴 Ссылка на встречу здесь не ошибка. Человек вставил её в поле ссылки —
 * значит хочет расшифровку этой встречи, а какой сервис за это отвечает, его
 * не касается. Возвращаем `kind: 'meeting'`, и экран переключает действие на
 * «позвать бота» с той же ссылкой.
 */

// Площадки встреч в отказе — те, что включены сейчас: называть выключенную
// значит обещать то, на что бот ответит тем же отказом.
function refusal() {
  const meetings = supportedPlatformsText();
  return `Не понимаю эту ссылку. Принимаем записи: ${SUPPORTED_VIDEO_PLATFORMS}.`
    + (meetings ? ` Ссылки на встречи — ${meetings}: на них отправим бота.` : '');
}

/**
 * Спрос на площадки, которых нет: хост отказанной ссылки уходит в ui_events
 * (`link_unsupported`), сводка — в метриках платформы.
 *
 * 🔴 Только хост. Путь и параметры ссылки на личный файл — это доступ к нему
 * (ключи Dropbox, id Google Drive), в журнал им нельзя.
 * Журнал не должен мешать отказу: не ждём и глотаем любую ошибку, даже
 * синхронную.
 */
function reportUnsupported(host, log) {
  if (!host) return;
  try {
    Promise.resolve(log('link_unsupported', { component: 'recognition', meta: { host } }))
      .catch(() => {});
  } catch {
    // журнал спроса не должен ломать отказ
  }
}

/**
 * `diarize` и число участников — тот же выбор «по участникам», что у файла:
 * bit-fetch отдаёт скачанное в bit-diarize-v3 вместо parakeet. Без галочки —
 * ни флага, ни числа: parakeet их не понимает.
 *
 * `language` — язык речи без галочки: 'ru' уходит в v3, другой — в parakeet.
 * 'auto' не шлём: тогда решает bit-fetch (язык дорожки у площадки, иначе по
 * названию). С галочкой язык не нужен — участников разделяет только русский
 * конвейер.
 */
async function start({ url, diarize = false, numSpeakers = null, minSpeakers = null,
                       maxSpeakers = null, language = 'auto' },
                     { fetch, baseUrl, log = defaultLog }) {
  const clean = extractLink(url);
  // Список включённых площадок — до разбора: иначе ссылка на выключенную в
  // админке площадку ушла бы к боту и вернулась отказом уже оттуда. Ждём не
  // дольше полутора секунд и только первый запрос: ссылка на видео от бота не
  // зависит, и лежащий бот не должен её тормозить (awaitMeetingPlatforms).
  await awaitMeetingPlatforms();
  const { kind, host } = routeLink(clean);

  if (kind === 'meeting') return { kind: 'meeting', url: clean };
  if (kind !== 'video') {
    reportUnsupported(host, log);
    throw new Error(refusal());
  }

  let data;
  try {
    data = await fetch.submitUrl(baseUrl, null, clean, diarize
      ? { diarize: true, ...speakerParams({ diarize, numSpeakers, minSpeakers, maxSpeakers }) }
      : (language && language !== 'auto' ? { language } : {}));
  } catch (e) {
    // 🔴 Сервер объясняет причину в detail: лимит одновременных загрузок,
    // неподдержанный хост, слишком длинное видео. «Request failed with status
    // code 429» вместо этого не говорит человеку ничего.
    throw new Error(e?.response?.data?.detail || 'Не удалось забрать запись по ссылке');
  }
  // Длительность и адрес нужны записи в Истории: без них ссылка сохранялась
  // как «Файл» без длительности (обращение fe94f8a8, 02.10.2026). У облачного
  // диска bit-fetch длительности до скачивания не знает — тогда null.
  return { fetchId: data.fetch_id, title: data.title || null,
           duration: data.duration || null, url: clean, kind: 'link' };
}

/**
 * Ждёт скачивания, затем — готовности распознавания.
 *
 * bit-fetch сам передаёт скачанное дальше и отдаёт task_id, поэтому ожидание
 * двухступенчатое: сперва загрузка, потом обычный опрос статуса.
 */
async function watch(task, { fetch, baseUrl, asrBaseUrl, diarizeBaseUrl, v3BaseUrl, get,
                             onProgress, onStage, sleep, signal, onTaskId }) {
  const stage = (name) => { if (onStage) onStage(name); };
  stage('Скачивание');
  const fetched = await fetch.waitFetch(baseUrl, task.fetchId, { onProgress, signal });
  const taskId = fetched.task_id;
  // 🔴 Идентификатор задачи — УЖЕ у распознавания, не у bit-fetch: статус и
  // текст спрашиваются там. У кого именно, говорит engine; без него (bit-fetch
  // до 02.10.2026) — parakeet. Адреса приходят снаружи: имена переменных
  // окружения знает только экран. v3 — русская речь без участников (с 24.10.2026).
  const engineUrl = { diarize: diarizeBaseUrl, v3: v3BaseUrl }[fetched.engine] || asrBaseUrl;
  // Экрану нужен этот ключ до конца работы: отменять после скачивания придётся
  // уже задачу распознавания, а её номер и сервис известны только здесь.
  if (onTaskId) onTaskId(taskId, engineUrl);

  // Скачали — дальше распознавание, и это надо сказать: иначе человек всю
  // работу видит одну и ту же надпись. Сначала «Очередь»: распознавание
  // начнётся, когда освободится слот, и об этом скажет опрос (poll.js).
  stage('Очередь');
  if (onProgress) onProgress(0);
  // 🔴 Дальше — ровно как у файла: плоский текст parakeet и v3 разбирается на
  // реплики с таймкодом до секунд, реплики diarize — на участников с именами.
  // До 02.10.2026 текст ссылки уходил одним куском с миллисекундами
  // (обращение fe94f8a8).
  const done = await fileSource.watch({ taskId, baseUrl: engineUrl },
                                      { get, onProgress, onStage, sleep, signal });
  // Длительность облачного диска bit-fetch узнаёт только после скачивания —
  // берём её из итогового статуса, иначе в Истории «—».
  // engine — для подписи модели на экране результата: по репликам v3 и parakeet
  // не различить.
  return { ...done, engine: fetched.engine || 'parakeet',
           duration: fetched.duration || task.duration || null };
}

/**
 * Отмена по ссылке — две задачи, а не одна.
 *
 * 🔴 Слот bit-fetch держится час при лимите два на человека: без снятия
 * скачивания две передуманные ссылки выжигали лимит, и третья получала
 * «слишком много одновременных загрузок». Если скачанное уже уехало в
 * parakeet — там своя задача, и она считает на видеокарте.
 */
async function cancel(task, { fetch, cancelTask, baseUrl, asrBaseUrl }) {
  if (task?.fetchId) await fetch.cancelFetch(baseUrl, task.fetchId);
  // «По участникам» задача у diarize: её адрес экран кладёт в task.asrBaseUrl.
  if (task?.taskId) await cancelTask(task.asrBaseUrl || asrBaseUrl, task.taskId);
}

export const linkSource = { key: 'link', start, watch, cancel };
